import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * ArrowBack component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconArrowBack extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon cat-c-icon-bi-directional', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error',
      'cat-c-icon-bi-directional': true
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.5318 3.38531C12.8247 3.09242 12.8247 2.61754 12.5318 2.32465C12.2389 2.03176 11.764 2.03176 11.4711 2.32465L2.32612 11.4697C2.03322 11.7625 2.03322 12.2374 2.32612 12.5303L11.4711 21.6753C11.764 21.9682 12.2389 21.9682 12.5318 21.6753C12.8247 21.3824 12.8247 20.9075 12.5318 20.6147L4.66712 12.75H22.1429C22.5571 12.75 22.8929 12.4142 22.8929 12C22.8929 11.5858 22.5571 11.25 22.1429 11.25H4.66709L12.5318 3.38531Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-arrow-back') === undefined) {
  customElements.define('cat-icon-arrow-back', BlocksIconArrowBack);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-arrow-back': BlocksIconArrowBack;
  }
}
